import { Box } from '@mui/material';
import { FormikProps } from 'formik';
import * as Yup from 'yup';

import { TipoLojaAutoComplete } from 'components/autocompletes';
import { CissNumberField, CissTextField } from 'components/fields';
import { useLoaderContext } from 'context/LoaderContext';
import { Block, FormPage } from 'components/page';
import { BiGrupoUsuarioAutoComplete } from 'pages/bi/components/form/BiGrupoUsuarioAutoComplete';
import { BiTipoPainelAutoComplete } from 'pages/bi/components/form/BiTipoPainelAutoComplete';
import { useMutateBiForm } from 'pages/bi/hooks';
import { ConfigBi, initialValues } from 'pages/bi/types';

export function BiFormComp() {
    const { handleSubmit, isLoading } = useMutateBiForm();

    useLoaderContext(isLoading);

    const validationSchema = Yup.object({
        dsConfigBi: Yup.string().required(),
        vlAltura: Yup.number().required().min(1, 'O campo deve ser maior que 0.'),
        cdPainelBi: Yup.string().required(),
        nrSequencia: Yup.number().required().min(1, 'O campo deve ser maior que 0.'),
        tipoLoja: Yup.array().required().min(1, 'O campo é obrigatório.'),
        grupoUsuario: Yup.array().required().min(1, 'O campo é obrigatório.'),
        tipoPainelBi: Yup.object().required(),
        dsUrl: Yup.string().required().url(),
        dsUrlMobile: Yup.string().nullable().url(),
        vlAlturaMobile: Yup.number().when('dsUrlMobile', {
            is: (value: string | null) => Boolean(value),
            then: (schema) => schema.required().min(1, 'O campo deve ser maior que 0.'),
            otherwise: (schema) => schema.nullable(),
        }),
    });

    return (
        <FormPage
            fgAtivo
            goBackButton
            title="Painél de BI"
            url="/config/configbiwrapper"
            values={initialValues}
            onSubmit={handleSubmit}
            validationSchema={validationSchema}
        >
            {({ values, errors, setFieldValue }: FormikProps<ConfigBi>) => (
                <Block sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <CissTextField
                                label="Descrição"
                                sx={{ flex: 1 }}
                                value={values.dsConfigBi}
                                error={errors.dsConfigBi}
                                onChange={(e) => setFieldValue('dsConfigBi', e.target.value)}
                            />

                            <CissTextField
                                label="Código"
                                sx={{ flex: 0.5 }}
                                value={values.cdPainelBi}
                                error={errors.cdPainelBi}
                                onChange={(e) => setFieldValue('cdPainelBi', e.target.value)}
                            />

                            <CissNumberField
                                label="Posição"
                                preventEmptyField={false}
                                sx={{ flex: 0.5 }}
                                value={values.nrSequencia}
                                error={errors.nrSequencia}
                                onChange={(e) => setFieldValue('nrSequencia', Number(e.target.value))}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <TipoLojaAutoComplete
                                label="Tipo de loja"
                                multiple
                                sx={{ flex: 0.5 }}
                                value={values.tipoLoja}
                                error={errors.tipoLoja as string}
                                onChange={(e, value) => setFieldValue('tipoLoja', value)}
                            />

                            <BiTipoPainelAutoComplete
                                sx={{ flex: 0.5 }}
                                value={values.tipoPainelBi}
                                error={errors.tipoPainelBi as string}
                                onChange={(e, value) => setFieldValue('tipoPainelBi', value)}
                            />

                            <BiGrupoUsuarioAutoComplete
                                sx={{ flex: 1 }}
                                multiple
                                value={values.grupoUsuario}
                                error={errors.grupoUsuario as string}
                                onChange={(e, value) => setFieldValue('grupoUsuario', value)}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <CissNumberField
                                label="Altura desktop (px)"
                                sx={{ width: { xs: '100%', md: 140 } }}
                                value={values.vlAltura}
                                error={errors.vlAltura}
                                onChange={(e) => setFieldValue('vlAltura', Number(e.target.value))}
                            />

                            <CissTextField
                                label="Url desktop"
                                sx={{ flex: 1 }}
                                value={values.dsUrl}
                                error={errors.dsUrl as string}
                                onChange={(e) => setFieldValue('dsUrl', e.target.value)}
                            />
                        </Box>

                        <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2 }}>
                            <CissNumberField
                                label="Altura mobile (px)"
                                preventEmptyField={false}
                                value={values.vlAlturaMobile}
                                error={errors.vlAlturaMobile}
                                sx={{ width: { xs: '100%', md: 140 } }}
                                onChange={(e) => setFieldValue('vlAlturaMobile', Number(e.target.value))}
                            />

                            <CissTextField
                                label="Url mobile"
                                sx={{ flex: 1 }}
                                value={values.dsUrlMobile}
                                error={errors.dsUrlMobile as string}
                                onChange={(e) => setFieldValue('dsUrlMobile', e.target.value)}
                            />
                        </Box>
                    </Block>
            )}
        </FormPage>
    );
}
